<script lang="ts">
  /**
   * The strategic break.
   *
   * Only ever shown to the batting side, because only the batting side's answer
   * does anything — the engine applies an aggression setting when
   * `side === 'batting'` and ignores it otherwise. A slider on the bowling
   * break would be a control that changes nothing.
   *
   * The copy says the trade out loud without quoting a number at it: going
   * harder raises boundary chance and wicket chance together, and no setting
   * buys runs for free.
   */
  import type { Match } from '../lib/match.svelte';

  interface Props {
    match: Match;
  }
  let { match }: Props = $props();

  const prompt = $derived(match.prompt?.kind === 'TIMEOUT' ? match.prompt : null);

  // Opens where a CPU captain would set it, so leaving it alone is a sensible
  // default rather than an arbitrary one.
  let value = $state(0);
  $effect(() => {
    if (prompt) value = match.suggestedAggression();
  });
</script>

{#if prompt}
  <div class="sheet open" aria-label="Strategic break">
    <div class="scrim"></div>
    <div class="panel" role="dialog" aria-modal="true">
      <div class="handle"></div>
      <h2>Strategic break</h2>
      <p class="muted">
        {match.chase
          ? `${match.chase.runsRequired} needed from ${match.chase.ballsRemaining}. How hard from here?`
          : 'How hard for the overs ahead?'}
      </p>

      <input
        type="range"
        min="-1"
        max="1"
        step="0.25"
        bind:value
        aria-label="Aggression"
        style="width:100%;margin:var(--space-4) 0 var(--space-2)"
      />
      <div style="display:flex;justify-content:space-between;font-size:var(--text-xs);color:var(--fg-muted)">
        <span>Consolidate</span><span>Go hard</span>
      </div>

      <div class="card" style="margin-top:var(--space-4)">
        <p style="margin:0;font-size:var(--text-sm)">
          Going harder buys boundaries and wickets together. Nothing here gets you runs for free.
        </p>
      </div>

      <button
        class="btn-primary"
        style="width:100%;margin-top:var(--space-3)"
        onclick={() => match.setAggression(value)}
      >
        Resume
      </button>
      {#if !prompt.forced}
        <button
          class="btn-ghost"
          style="width:100%;margin-top:var(--space-2)"
          onclick={() => match.declineTimeout()}
        >
          Not yet — save the break
        </button>
      {/if}
    </div>
  </div>
{/if}
